/**
 * FB-2026-0016 (มติ PO 3 พ.ย. 2026): ช่องปรับราคาเช่าในหน้าต่าง "ย้ายอุปกรณ์ไปโครงการอื่น"
 *
 * แสดงเฉพาะผู้มีสิทธิ์ rentals.billing_manage (ผู้เรียกตัดสิน) · ต่อรายการที่เลือกย้าย:
 * ราคาเดิม (อ่านอย่างเดียว = rental_rate ปัจจุบัน) · วันสิ้นสุดเก็บเงินราคาเดิม · ราคาใหม่ · วันเริ่มเก็บเงินราคาใหม่
 * เว้นวันได้ (ขึ้นคำเตือน "N วันไม่คิดเงิน") · ช่วงซ้อนไม่ได้ (backend ตอบ 400 ด้วย)
 * ไม่มีปุ่มปรับราคาแยกในหน้าสัญญา — ทำได้เฉพาะในหน้าต่างย้าย (มติ PO)
 */
import React from 'react';
import { RiMoneyDollarCircleLine, RiAlertLine } from 'react-icons/ri';
import { TransferContextItem } from '../../services/deviceMovementService';
import { PriceRowInput, formatRate, formatThaiDate, gapDays } from '../../utils/movementPriceChange';

interface Props {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  items: TransferContextItem[];
  rowFor: (itemId: number) => PriceRowInput;
  onChange: (itemId: number, patch: Partial<PriceRowInput>) => void;
  errors: { [key: string]: string };
  lastBilledPeriodEnd?: string | null;
  fromSiteName?: string | null;
  disabled?: boolean;
}

const MovementPriceChangeFields: React.FC<Props> = ({
  enabled,
  onToggle,
  items,
  rowFor,
  onChange,
  errors,
  lastBilledPeriodEnd,
  fromSiteName,
  disabled,
}) => (
  <div className="space-y-3">
    <label className="flex items-start gap-2 bg-secondary-50 border border-secondary-200 rounded-lg p-3 cursor-pointer">
      <input
        type="checkbox"
        checked={enabled}
        onChange={(e) => onToggle(e.target.checked)}
        className="w-4 h-4 mt-0.5 text-primary-600 focus:ring-primary-500 rounded"
        disabled={disabled}
      />
      <span className="text-sm text-secondary-800">
        <span className="inline-flex items-center font-medium">
          <RiMoneyDollarCircleLine className="w-4 h-4 mr-1 text-secondary-500" aria-hidden="true" />
          มีการปรับราคาเช่า
        </span>
        <span className="block text-xs text-secondary-600 mt-0.5">
          ใบแจ้งหนี้จะคิดราคาเดิมถึงวันสิ้นสุดเก็บเงินราคาเดิม (ชื่อโครงการเดิม) และราคาใหม่ตั้งแต่วันเริ่มเก็บเงินราคาใหม่
          {lastBilledPeriodEnd && ` · ออกใบแจ้งหนี้แล้วถึง ${formatThaiDate(lastBilledPeriodEnd)}`}
        </span>
      </span>
    </label>

    {enabled && (
      <div className="space-y-3" data-testid="movement-price-rows">
        {items.length === 0 && (
          <p className="text-sm text-secondary-500">เลือกรายการที่จะย้ายก่อน</p>
        )}
        {items.map((item) => {
          const id = item.rental_item_id;
          const row = rowFor(id);
          const gap = gapDays(row.oldEnd, row.newStart);
          const err = errors[`price_${id}`];
          return (
            <div key={id} className="border border-secondary-200 rounded-md p-3 bg-white">
              <p className="text-sm font-medium text-secondary-900 mb-2">
                {item.device_name || item.product_name || `รายการ #${id}`}
                {item.device_serial && (
                  <span className="text-xs text-secondary-500 font-normal"> · S/N: {item.device_serial}</span>
                )}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <span className="block text-xs font-medium text-secondary-700 mb-1">
                    ราคาเดิม (บาท/เดือน){fromSiteName ? ` · ${fromSiteName}` : ''}
                  </span>
                  <p className="input bg-secondary-50 text-secondary-700" aria-readonly="true">
                    {formatRate(item.rental_rate)}
                  </p>
                </div>
                <div>
                  <label htmlFor={`price-old-end-${id}`} className="block text-xs font-medium text-secondary-700 mb-1">
                    วันที่สิ้นสุดเก็บเงินราคาเดิม <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={`price-old-end-${id}`}
                    type="date"
                    value={row.oldEnd}
                    min={lastBilledPeriodEnd || undefined}
                    onChange={(e) => onChange(id, { oldEnd: e.target.value })}
                    className="input"
                    disabled={disabled}
                    aria-invalid={err ? true : undefined}
                    aria-describedby={err ? `price-error-${id}` : undefined}
                  />
                </div>
                <div>
                  <label htmlFor={`price-new-rate-${id}`} className="block text-xs font-medium text-secondary-700 mb-1">
                    ราคาใหม่ (บาท/เดือน) <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={`price-new-rate-${id}`}
                    type="text"
                    inputMode="decimal"
                    value={row.newRate}
                    placeholder="เช่น 1800.00"
                    onChange={(e) => onChange(id, { newRate: e.target.value })}
                    className="input"
                    disabled={disabled}
                    aria-invalid={err ? true : undefined}
                    aria-describedby={err ? `price-error-${id}` : undefined}
                  />
                </div>
                <div>
                  <label htmlFor={`price-new-start-${id}`} className="block text-xs font-medium text-secondary-700 mb-1">
                    วันที่เริ่มเก็บเงินราคาใหม่ <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={`price-new-start-${id}`}
                    type="date"
                    value={row.newStart}
                    min={row.oldEnd || undefined}
                    onChange={(e) => onChange(id, { newStart: e.target.value })}
                    className="input"
                    disabled={disabled}
                    aria-invalid={err ? true : undefined}
                    aria-describedby={err ? `price-error-${id}` : undefined}
                  />
                </div>
              </div>
              {gap > 0 && !err && (
                <p className="mt-2 flex items-center text-xs text-warning-800 bg-warning-50 border border-warning-200 rounded px-2 py-1">
                  <RiAlertLine className="w-4 h-4 mr-1 flex-shrink-0" aria-hidden="true" />
                  เว้น {gap} วันไม่คิดเงิน ({formatThaiDate(row.oldEnd)} → {formatThaiDate(row.newStart)})
                </p>
              )}
              {err && (
                <p id={`price-error-${id}`} role="alert" className="mt-2 text-xs text-danger-700">
                  {err}
                </p>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export default MovementPriceChangeFields;
